import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  CITY_SCOPE_BRAND_ALTERNATE_NAMES,
  CITY_SCOPE_BRAND_KEYWORDS,
  CITY_SCOPE_BRAND_NAME,
} from '../lib/brandSeo';
import { SITE_URL, toAbsoluteSiteUrl } from '../lib/siteUrl';

/**
 * Meta component for SEO and document head management
 * This component centralizes all meta tags, title, and other head elements
 */
const Meta = ({
  title = `${CITY_SCOPE_BRAND_NAME} | Urban Planning Data Visualization`,
  description = `Search local planning applications, development proposals, maps, addresses, postcodes, references, and weekly planning updates with ${CITY_SCOPE_BRAND_NAME}.`,
  keywords =
    `${CITY_SCOPE_BRAND_KEYWORDS}, planning applications, local planning applications, planning application search, planning permission, development tracker, address planning search, postcode planning search, planning reference search, Edinburgh planning applications, Edinburgh planning, City of Edinburgh planning, urban planning, city development, data visualization, scotland, GIS, mapping, open data, property analytics, local development, architecture, land use, building permits, UK planning, community engagement, site analysis, zoning, urban analytics, city mapping, construction trends`,
  ogImage = 'https://www.city-scope.co.uk/city-scope-social-v2.jpg',
  ogImageAlt = title,
  ogImageType = 'image/jpeg',
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogType = 'website',
  author = `${CITY_SCOPE_BRAND_NAME} Team`,
  publishedTime,
  modifiedTime,
  section,
  tags,
  // Prefer `canonical` (used across pages) but keep `canonicalUrl` for backward compat.
  canonical,
  canonicalUrl,
  noCanonical = false,
  noindex = false,
  nofollow = false,
  structuredData = null,
}) => {
  const router = useRouter();

  const canonicalHref = (() => {
    const rawInput = canonical ?? canonicalUrl ?? router?.asPath ?? '/';
    const raw = typeof rawInput === 'string' ? rawInput : String(rawInput ?? '/');
    const trimmed = raw.trim() || '/';
    const noHash = trimmed.split('#')[0];
    const noQuery = noHash.split('?')[0];

    if (/^https?:\/\//i.test(noQuery)) return noQuery;
    return toAbsoluteSiteUrl(noQuery);
  })();

  const robots = `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}, max-snippet:-1, max-image-preview:large, max-video-preview:-1`;
  const cityScopeSameAs = [
    'https://github.com/RisingAce/city-scope',
  ];
  const cityScopeSubjectOf = [
    {
      '@type': 'NewsArticle',
      url: 'https://www.deadlinenews.co.uk/2025/06/06/edinburgh-local-creates-handy-tool-to-view-planning-applications-across-city/',
      name: 'Edinburgh local creates handy tool to view planning applications across city',
    },
    {
      '@type': 'Article',
      url: 'https://edinburghminute.substack.com/p/edinburgh-minute-weekend-edition-6-june-2025',
      name: 'Edinburgh Minute coverage of City Scope',
    },
    {
      '@type': 'Article',
      url: 'https://www.linkedin.com/pulse/city-scope-one-year-later-harry-williams-5ec1e',
      name: 'City Scope, one year later',
    },
  ];

  const baseStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: CITY_SCOPE_BRAND_NAME,
      alternateName: CITY_SCOPE_BRAND_ALTERNATE_NAMES,
      url: SITE_URL,
      logo: toAbsoluteSiteUrl('/MainLogo.svg'),
      description:
        'Search and analysis platform for local planning applications, development proposals, addresses, postcodes, maps, and weekly planning updates.',
      keywords: CITY_SCOPE_BRAND_KEYWORDS,
      founder: {
        '@type': 'Person',
        name: 'Harry Williams',
      },
      creator: {
        '@type': 'Person',
        name: 'Harry Williams',
      },
      sameAs: cityScopeSameAs,
      subjectOf: cityScopeSubjectOf,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: CITY_SCOPE_BRAND_NAME,
      alternateName: CITY_SCOPE_BRAND_ALTERNATE_NAMES,
      description:
        'Search local planning applications by address, postcode, reference, date, status, and location.',
      keywords: CITY_SCOPE_BRAND_KEYWORDS,
      publisher: {
        '@id': `${SITE_URL}/#organization`
      },
    },
  ];

  const pageStructuredData = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />
      <meta name="author" content={author} />
      <meta name="publisher" content={CITY_SCOPE_BRAND_NAME} />
      <meta name="application-name" content={CITY_SCOPE_BRAND_NAME} />
      <meta name="subject" content="Urban Planning Data Visualization" />
      <meta name="rating" content="General" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
      
      {/* Favicon and App Icons - Comprehensive set for all browsers and devices */}
      <link rel="icon" type="image/svg+xml" href="/MainLogo.svg" />
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/manifest.json" />
      <meta name="msapplication-TileImage" content="/mstile-150x150.png" />
      <meta name="msapplication-TileColor" content="#6d28d9" />
      
      {/* Open Graph / Social Media */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalHref} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:type" content={ogImageType} />
      {ogImageWidth ? <meta property="og:image:width" content={String(ogImageWidth)} /> : null}
      {ogImageHeight ? <meta property="og:image:height" content={String(ogImageHeight)} /> : null}
      <meta property="og:site_name" content={CITY_SCOPE_BRAND_NAME} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:locale:alternate" content="en_US" />
      {ogType === 'article' && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          {Array.isArray(tags) && tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* iOS Specific */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={CITY_SCOPE_BRAND_NAME} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalHref} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <meta name="twitter:creator" content="@cityscopeuk" />
      
      {/* Canonical URL */}
      {!noCanonical && <link rel="canonical" href={canonicalHref} />}
      
      {/* Additional Meta */}
      <meta name="theme-color" content="#6d28d9" />
      <meta name="format-detection" content="telephone=no" />
      {[...baseStructuredData, ...pageStructuredData].map((item, index) => (
        <script
          key={`structured-data-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(item)
          }}
        />
      ))}
    </Head>
  );
};

export default Meta;
